import { useStore, selectCreature } from '@/infra/store';
import { computeHealth } from '@/domain/care/health';
import { resolveBehaviorProfile } from '@/domain/care/behavior';
import { useT } from '@/infra/i18n';

const FONT_PIXEL = '"Courier New", monospace';
const ASSET_ROOT = '/assets/generated/house';

interface BarProps {
  label: string;
  value: number;
  color: string;
  testId: string;
}

function Bar({ label, value, color, testId }: BarProps) {
  const v = Math.max(0, Math.min(100, value));
  const critical = v < 25;
  return (
    <div data-testid={testId} style={barItem}>
      <div style={rowLabel}>
        <span style={{ fontFamily: FONT_PIXEL }}>{label}</span>
        <span style={{ ...numStyle, color: critical ? '#ff5566' : '#e8e0d0' }}>
          {Math.round(v).toString().padStart(3, '0')}
        </span>
      </div>
      <div style={track}>
        <div
          style={{
            width: `${v}%`,
            height: '100%',
            background: color,
            borderRadius: 4,
            boxShadow: `0 0 10px ${color}55`,
            animation: critical ? 'pixelBlink 0.6s steps(2) infinite' : 'none',
          }}
        />
      </div>
    </div>
  );
}

export function StatBars() {
  const t = useT();
  const c = useStore(selectCreature);
  if (!c) return null;
  const health = computeHealth(c.state, c.lifecycle.stage);
  const statusBadges: Array<{ icon: string; label: string }> = [];
  if (c.state.dead) statusBadges.push({ icon: 'icon-close.png', label: t('status.dead') });
  if (c.state.tantrum) statusBadges.push({ icon: 'icon-discipline.png', label: t('status.tantrum') });
  if (c.state.sick) statusBadges.push({ icon: 'icon-heal.png', label: t('status.sick') });
  const behavior = c.lifecycle.stage === 'egg' || c.state.dead ? null : resolveBehaviorProfile(c);
  const showBehaviorMood =
    behavior !== null &&
    behavior.mood !== 'content' &&
    behavior.mood !== 'sick' &&
    behavior.mood !== 'tantrum';
  return (
    <div data-testid="stat-bars" style={panel}>
      <div style={panelInner}>
        <div style={headerRow}>
          <h3 style={{ ...nameTitle, color: c.state.dead ? '#ff5566' : '#ffd6b3' }}>
            {c.name}
          </h3>
          <span style={stageBadge}>{t(`stage.${c.state.dead ? 'dead' : c.lifecycle.stage}`)}</span>
        </div>
        {(statusBadges.length > 0 || showBehaviorMood) && (
          <div style={statusRow}>
            {showBehaviorMood && (
              <span data-testid="behavior-mood" style={moodChip}>
                {t(`behavior.mood.${behavior.mood}`)}
              </span>
            )}
            {statusBadges.map((b) => (
              <span key={b.label} style={statusChip}>
                <img src={`${ASSET_ROOT}/${b.icon}`} alt="" draggable={false} style={statusIcon} />
                {b.label}
              </span>
            ))}
          </div>
        )}
        <div style={barsGrid}>
        <Bar label={t('action.feed')} value={c.state.hunger} color="#f0c870" testId="bar-hunger" />
        <Bar label={t('action.play')} value={c.state.happiness} color="#ff6eb4" testId="bar-happiness" />
        <Bar label={t('combat.hp')} value={health} color="#6cf08f" testId="bar-health" />
        <Bar label={t('action.discipline')} value={c.state.discipline} color="#ff8844" testId="bar-discipline" />
        <Bar label="Peso" value={c.state.weight} color="#88aaff" testId="bar-weight" />
        </div>
        {c.state.poopCount > 0 && (
          <div style={poopRow}>
            {Array.from({ length: Math.min(c.state.poopCount, 5) }).map((_, i) => (
              <img key={i} src={`${ASSET_ROOT}/prop-poop.png`} alt="" draggable={false} style={poopIcon} />
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes pixelBlink {0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </div>
  );
}

const panel: React.CSSProperties = {
  position: 'fixed',
  top: 58,
  left: 12,
  background: 'rgba(16, 18, 32, 0.84)',
  border: '1px solid rgba(255, 226, 188, 0.52)',
  borderRadius: 10,
  width: 'min(336px, calc(100vw - 24px))',
  zIndex: 90,
  imageRendering: 'pixelated',
  boxShadow: '0 10px 26px rgba(0, 0, 0, 0.24)',
  overflow: 'hidden',
  backdropFilter: 'blur(4px)',
};
const panelInner: React.CSSProperties = {
  padding: '13px 14px 12px',
};
const headerRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
};
const nameTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontFamily: FONT_PIXEL,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};
const stageBadge: React.CSSProperties = {
  fontFamily: FONT_PIXEL,
  fontSize: 9,
  color: '#14142a',
  background: '#ffd6b3',
  padding: '3px 8px',
  borderRadius: 999,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  boxShadow: '0 2px 0 rgba(0,0,0,0.16)',
};
const statusRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 5,
  flexWrap: 'wrap',
};
const statusChip: React.CSSProperties = {
  fontFamily: FONT_PIXEL,
  fontSize: 11,
  color: '#ffcc66',
  background: 'rgba(255,204,102,0.16)',
  padding: '4px 8px 4px 5px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  textTransform: 'uppercase',
};
const moodChip: React.CSSProperties = {
  fontFamily: FONT_PIXEL,
  fontSize: 11,
  color: '#c8f7ff',
  background: 'rgba(82, 203, 255, 0.16)',
  border: '1px solid rgba(154, 226, 255, 0.28)',
  padding: '5px 8px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  textTransform: 'uppercase',
  textShadow: '0 1px 0 rgba(0,0,0,0.75)',
};
const statusIcon: React.CSSProperties = {
  width: 34,
  height: 34,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const barsGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 10,
  rowGap: 8,
  marginTop: 8,
};
const barItem: React.CSSProperties = {
  minWidth: 0,
};
const rowLabel: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontFamily: FONT_PIXEL,
  fontSize: 10,
  color: '#f3ead8',
  marginBottom: 3,
  gap: 6,
  textShadow: '0 1px 0 rgba(0,0,0,0.8)',
};
const numStyle: React.CSSProperties = {
  fontFamily: FONT_PIXEL,
  fontWeight: 700,
};
const track: React.CSSProperties = {
  height: 8,
  background: 'rgba(4, 6, 14, 0.76)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 999,
  overflow: 'hidden',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.55)',
};
const poopRow: React.CSSProperties = {
  marginTop: 8,
  display: 'flex',
  gap: 4,
};
const poopIcon: React.CSSProperties = {
  width: 30,
  height: 30,
  objectFit: 'contain',
  imageRendering: 'auto',
};
